import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { Rating } from "primereact/rating";
import { ProgressBar } from "primereact/progressbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { useToast } from "../../utils/toast";
import { useAuth } from "../../utils/auth";
import dynamicSurveyService, { DynamicAnswerSubmitDto } from "../../services/forms/dynamic.service";

const DynamikAnketDoldur = () => {
  const { id } = useParams<{ id: string }>();
  const surveyId = Number(id);
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();

  const [answers, setAnswers] = useState<Record<number, DynamicAnswerSubmitDto>>({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["dynamicSurveyDetail", surveyId],
    queryFn: () => dynamicSurveyService.getSurveyDetail(surveyId),
    enabled: !!surveyId,
  });

  const survey = data?.result?.data;

  if (isLoading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-6">
        <i className="pi pi-exclamation-triangle text-yellow-500" style={{ fontSize: "2rem" }} />
        <p className="text-600 mt-3">Anket bulunamadı.</p>
        <Button label="Geri Dön" icon="pi pi-arrow-left" outlined onClick={() => navigate("/app/anketler")} />
      </div>
    );
  }

  if (survey.has_submitted || submitted) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-6">
        <div
          className="flex flex-column align-items-center justify-content-center border-round-xl p-5"
          style={{ background: "var(--green-50)", border: "2px solid var(--green-300)", maxWidth: "480px", width: "100%" }}
        >
          <i className="pi pi-check-circle text-green-500" style={{ fontSize: "3rem" }} />
          <h3 className="text-900 font-bold mt-3 mb-1 text-xl">Teşekkürler!</h3>
          <p className="text-600 text-center m-0">Bu ankete daha önce katıldınız. Cevaplarınız başarıyla kaydedildi.</p>
          <Button
            label="Anket Listesine Dön"
            icon="pi pi-arrow-left"
            className="mt-4"
            onClick={() => navigate("/app/anketler")}
          />
        </div>
      </div>
    );
  }

  const questions = survey.questions ?? [];
  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  const updateTextAnswer = (questionId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, text_answer: value },
    }));
  };

  const updateSingleChoice = (questionId: number, choiceId: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, selected_choices: [choiceId] },
    }));
  };

  const updateMultiChoice = (questionId: number, choiceId: number, checked: boolean) => {
    setAnswers((prev) => {
      const current = prev[questionId]?.selected_choices ?? [];
      const updated = checked ? [...current, choiceId] : current.filter((id) => id !== choiceId);
      return { ...prev, [questionId]: { question_id: questionId, selected_choices: updated } };
    });
  };

  const updateRating = (questionId: number, value: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, rating_answer: value },
    }));
  };

  const handleSubmit = async () => {
    if (!auth.getUser().access_token) {
      toast.show("Anket doldurmak için giriş yapmalısınız.", "error");
      navigate("/loginsebis");
      return;
    }

    setSubmitting(true);
    try {
      const answersArray = Object.values(answers);
      const res = await dynamicSurveyService.submitSurveyResponse(surveyId, { answers: answersArray });

      if (res.result && !res.result.error) {
        setSubmitted(true);
        setShowConfirm(false);
        toast.show("Cevaplarınız başarıyla kaydedildi.", "success");
      } else {
        const errMsg = res.result?.error ?? "Bir hata oluştu.";
        toast.show(typeof errMsg === "string" ? errMsg : "Bir hata oluştu.", "error");
      }
    } catch {
      toast.show("Bir hata oluştu. Lütfen tekrar deneyin.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3 md:p-4" style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div className="mb-4">
        <Button
          icon="pi pi-arrow-left"
          label="Geri"
          text
          severity="secondary"
          className="mb-2 p-0"
          onClick={() => navigate("/app/anketler")}
        />
        <h2 className="text-900 font-bold text-2xl m-0">{survey.title}</h2>
        {survey.description && <p className="text-500 mt-1 mb-0">{survey.description}</p>}
      </div>

      <div className="mb-4">
        <div className="flex align-items-center justify-content-between mb-2 text-sm text-500">
          <span>{answeredCount} / {questions.length} soru cevaplandı</span>
          <span>%{progress}</span>
        </div>
        <ProgressBar value={progress} showValue={false} style={{ height: "6px" }} />
      </div>

      <div className="flex flex-column gap-3">
        {questions.map((question, index) => {
          const currentAnswer = answers[question.id!];
          const isAnswered = !!currentAnswer;

          return (
            <Card
              key={question.id}
              className="border-round-xl shadow-1"
              style={{ borderLeft: `4px solid ${isAnswered ? "var(--green-400)" : "var(--surface-300)"}` }}
            >
              <div className="flex align-items-start gap-2 mb-3">
                <span
                  className="flex align-items-center justify-content-center border-circle font-bold text-sm flex-shrink-0"
                  style={{
                    width: "28px",
                    height: "28px",
                    background: isAnswered ? "var(--green-100)" : "var(--surface-200)",
                    color: isAnswered ? "var(--green-700)" : "var(--text-color-secondary)",
                  }}
                >
                  {isAnswered ? <i className="pi pi-check text-xs" /> : index + 1}
                </span>
                <div className="flex-1">
                  <p className="text-900 font-medium m-0" style={{ lineHeight: "1.5" }}>
                    {question.text}
                  </p>
                  <Tag
                    className="mt-1"
                    value={
                      question.question_type === "text"
                        ? "Serbest Metin"
                        : question.question_type === "single_choice"
                        ? "Tek Seçmeli"
                        : question.question_type === "multiple_choice"
                        ? "Çok Seçmeli"
                        : "Derecelendirme"
                    }
                    severity="info"
                    style={{ fontSize: "0.7rem" }}
                  />
                </div>
              </div>

              {question.question_type === "text" && (
                <InputTextarea
                  value={currentAnswer?.text_answer ?? ""}
                  onChange={(e) => updateTextAnswer(question.id!, e.target.value)}
                  rows={3}
                  placeholder="Cevabınızı buraya yazın..."
                  className="w-full"
                  autoResize
                />
              )}

              {question.question_type === "single_choice" && (
                <div className="flex flex-column gap-2">
                  {question.choices?.map((choice) => (
                    <div key={choice.id} className="flex align-items-center gap-2">
                      <RadioButton
                        inputId={`q${question.id}_c${choice.id}`}
                        value={choice.id}
                        checked={currentAnswer?.selected_choices?.[0] === choice.id}
                        onChange={() => updateSingleChoice(question.id!, choice.id!)}
                      />
                      <label htmlFor={`q${question.id}_c${choice.id}`} className="cursor-pointer text-900">
                        {choice.text}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {question.question_type === "multiple_choice" && (
                <div className="flex flex-column gap-2">
                  {question.choices?.map((choice) => (
                    <div key={choice.id} className="flex align-items-center gap-2">
                      <Checkbox
                        inputId={`q${question.id}_c${choice.id}`}
                        value={choice.id}
                        checked={currentAnswer?.selected_choices?.includes(choice.id!) ?? false}
                        onChange={(e) => updateMultiChoice(question.id!, choice.id!, e.checked!)}
                      />
                      <label htmlFor={`q${question.id}_c${choice.id}`} className="cursor-pointer text-900">
                        {choice.text}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {question.question_type === "rating" && (
                <div className="flex flex-column align-items-start gap-1">
                  <Rating
                    value={currentAnswer?.rating_answer ?? 0}
                    onChange={(e) => updateRating(question.id!, e.value!)}
                    cancel={false}
                    stars={5}
                  />
                  <span className="text-500 text-xs">
                    {currentAnswer?.rating_answer
                      ? `${currentAnswer.rating_answer} / 5 seçildi`
                      : "1'den 5'e kadar değerlendirin"}
                  </span>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <div className="flex justify-content-end mt-4">
        <Button
          label="Anketi Gönder"
          icon="pi pi-send"
          size="large"
          disabled={answeredCount === 0}
          onClick={() => setShowConfirm(true)}
        />
      </div>

      <Dialog
        header="Anketi Gönder"
        visible={showConfirm}
        onHide={() => setShowConfirm(false)}
        style={{ width: "min(400px, 95vw)" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button label="İptal" icon="pi pi-times" outlined severity="secondary" onClick={() => setShowConfirm(false)} disabled={submitting} />
            <Button label="Gönder" icon="pi pi-send" loading={submitting} onClick={handleSubmit} />
          </div>
        }
      >
        <div className="flex flex-column gap-2">
          <p className="m-0 text-900">
            <strong>{answeredCount}</strong> soruyu cevapladınız ({questions.length - answeredCount} soru boş bırakıldı).
          </p>
          <p className="m-0 text-500 text-sm">Cevaplarınızı gönderdikten sonra değiştiremezsiniz. Onaylıyor musunuz?</p>
        </div>
      </Dialog>
    </div>
  );
};

export default DynamikAnketDoldur;
